import dynamic from 'next/dynamic';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useButtonAction } from '@/modules/promo';
import { getGameImagesByLocale } from '@/modules/promo/helpers/gamesByLocale';
import { useGetCommonStore } from '@/store';
import type { SlideGrid, SwiperSliderProps } from '../SwiperSlider';
import {
  type ITopGamesSliderProps,
  TopGamesSlide,
} from './components/TopGamesSlide';
import {
  DEFAULT_SLIDE_GRID,
  DESKTOP_GAMES_COUNT,
  desktopImages,
  IMG_DESKTOP_SIZE,
  IMG_MOBILE_SIZE,
  MOBILE_GAMES_COUNT,
  mobileImages,
} from './helpers';
import { ScTopGames } from './styled';

const DynamicSwiperSlider = dynamic<SwiperSliderProps<ITopGamesSliderProps>>(
  () => import('../SwiperSlider').then((mod) => mod.SwiperSlider),
  {
    ssr: false,
  }
);

export interface ITopGamesProps {
  showSlider?: boolean;
  srcImgs?: string;
  imgByLocation?: boolean;
  customImages?: string[];
  loopSlider?: boolean;
  deskImgSize?: number;
  mobImgSize?: number;
  slideGrid?: SlideGrid;
  slidesPerView?: number;
  slidesPerGroup?: number;
  spaceBetween?: number;
  fullMobWidth?: boolean;
  disableOnClick?: boolean;
  centeredSlides?: boolean;
  isWheelScroll?: boolean;
}

export const TopGames = ({
  showSlider,
  srcImgs,
  imgByLocation,
  customImages,
  loopSlider,
  deskImgSize,
  mobImgSize,
  slideGrid = DEFAULT_SLIDE_GRID,
  slidesPerView,
  slidesPerGroup = 1,
  spaceBetween = 12,
  fullMobWidth,
  disableOnClick,
  centeredSlides,
  isWheelScroll,
}: ITopGamesProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const { buttonAction } = useButtonAction();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const images =
    customImages || (isMobileDevice ? mobileImages : desktopImages);

  const size = isMobileDevice
    ? (mobImgSize ?? IMG_MOBILE_SIZE)
    : (deskImgSize ?? IMG_DESKTOP_SIZE);

  const imagesByLocation = useMemo(
    () => getGameImagesByLocale(language),
    [language]
  );

  const slidesWithImageProps = useMemo(() => {
    const baseImgs =
      customImages || (imgByLocation ? imagesByLocation : images);
    let imgs = baseImgs;

    if (isMobileDevice && imgByLocation && !customImages) {
      imgs = [...imgs, imgs[3]];
    }

    return imgs.map((img) => ({
      images: [img],
      size,
      srcImgs,
    }));
  }, [
    customImages,
    imgByLocation,
    imagesByLocation,
    images,
    isMobileDevice,
    size,
    srcImgs,
  ]);

  const trimmedImgs = useMemo(() => {
    if (customImages) return customImages;
    const baseImgs = imgByLocation ? imagesByLocation : images;
    return showSlider
      ? baseImgs
      : baseImgs.slice(
          0,
          isMobileDevice ? MOBILE_GAMES_COUNT : DESKTOP_GAMES_COUNT
        );
  }, [
    customImages,
    images,
    imagesByLocation,
    imgByLocation,
    isMobileDevice,
    showSlider,
  ]);

  return (
    <ScTopGames
      {...(disableOnClick ? {} : buttonAction)}
      customImages={customImages}
      disableOnClick={disableOnClick}
      fullMobWidth={fullMobWidth}
      showSlider={showSlider}
    >
      <div className="title">{t('game.topGames')}</div>
      {!showSlider && (
        <TopGamesSlide
          images={trimmedImgs}
          isWheelScroll={isWheelScroll}
          size={size}
          srcImgs={srcImgs}
        />
      )}
      {showSlider && (
        <DynamicSwiperSlider
          autoplay={{
            delay: 2500,
            pauseOnMouseEnter: true,
          }}
          centeredSlides={centeredSlides}
          loop={loopSlider}
          slide={TopGamesSlide}
          slideGrid={slideGrid}
          slides={slidesWithImageProps}
          slidesPerGroup={slidesPerGroup}
          slidesPerView={slidesPerView ?? 'auto'}
          spaceBetween={spaceBetween}
        />
      )}
    </ScTopGames>
  );
};
